import React from 'react';
import { Check, X, ArrowRight, Layers, Clock, BarChart3, Users, Shield, Briefcase } from 'lucide-react';
import { motion } from 'framer-motion';
export function SummitProduct() {
  return <div className="min-h-screen w-full bg-[#0B1120] text-slate-200 selection:bg-amber-500/30 font-sans" data-id="element-554">
      {/* 1. HERO */}
      <section className="relative pt-32 pb-20 px-6 md:px-12 max-w-7xl mx-auto text-center" data-id="element-555">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-96 bg-amber-500/5 blur-[120px] rounded-full pointer-events-none" data-id="element-556" />

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-557">
          <div className="inline-block px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-bold uppercase tracking-widest mb-6" data-id="element-558">
            Embedded Execution
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight" data-id="element-559">
            HIRE Summit
          </h1>
          <p className="text-xl md:text-2xl text-slate-400 max-w-3xl mx-auto mb-8 leading-relaxed" data-id="element-560">
            When recruiting execution becomes the constraint, HIRE runs it for
            you.
          </p>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto border-t border-slate-800 pt-8" data-id="element-561">
            Summit is designed for teams who want recruiting execution handled
            so leadership can stay focused on production and growth.
          </p>
        </motion.div>
      </section>

      {/* 2. THE EXECUTION BOTTLENECK */}
      <section className="py-20 px-6 md:px-12 max-w-4xl mx-auto border-t border-slate-800/50" data-id="element-562">
        <div className="text-center mb-12" data-id="element-563">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-564">
            The Execution Bottleneck
          </h2>
          <p className="text-slate-400 text-lg" data-id="element-565">
            At scale, recruiting doesn't fail because of traffic or systems.
          </p>
        </div>

        <div className="bg-slate-900/30 border border-slate-800 p-8 md:p-12 rounded-xl" data-id="element-566">
          <h3 className="text-sm text-amber-400 uppercase tracking-widest font-bold mb-6 text-center" data-id="element-567">
            Execution Constraints
          </h3>
          <ul className="space-y-4 max-w-2xl mx-auto" data-id="element-568">
            {['Leaders are pulled into interviews', 'Follow-up slows down', 'Calendars bottleneck', 'Recruiting becomes a second job'].map((item, idx) => <li key={idx} className="flex items-start text-slate-300" data-id="element-569">
                <X className="w-5 h-5 text-amber-500/50 mr-3 mt-0.5 flex-shrink-0" data-id="element-570" />
                <span data-id="element-571">{item}</span>
              </li>)}
          </ul>
          <p className="text-center text-slate-400 mt-8 pt-8 border-t border-slate-800" data-id="element-572">
            Summit exists to remove that bottleneck.
          </p>
        </div>
      </section>

      {/* 3. WHAT CHANGES WITH SUMMIT */}
      <section className="py-20 px-6 md:px-12 bg-slate-900/20 border-y border-slate-800" data-id="element-573">
        <div className="max-w-5xl mx-auto" data-id="element-574">
          <h2 className="text-3xl font-bold text-white mb-12 text-center" data-id="element-575">
            What Changes With Summit
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-id="element-576">
            <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-xl" data-id="element-577">
              <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center mb-4 border border-slate-700" data-id="element-578">
                <Shield className="w-6 h-6 text-amber-400" data-id="element-579" />
              </div>
              <h3 className="text-white font-bold mb-2" data-id="element-580">Execution Ownership</h3>
              <p className="text-slate-400 text-sm" data-id="element-581">
                Recruiting execution becomes HIRE's responsibility
              </p>
            </div>
            <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-xl" data-id="element-582">
              <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center mb-4 border border-slate-700" data-id="element-583">
                <Users className="w-6 h-6 text-amber-400" data-id="element-584" />
              </div>
              <h3 className="text-white font-bold mb-2" data-id="element-585">Managed Throughput</h3>
              <p className="text-slate-400 text-sm" data-id="element-586">
                Interview throughput is managed, not improvised
              </p>
            </div>
            <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-xl" data-id="element-587">
              <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center mb-4 border border-slate-700" data-id="element-588">
                <Briefcase className="w-6 h-6 text-amber-400" data-id="element-589" />
              </div>
              <h3 className="text-white font-bold mb-2" data-id="element-590">Protected Time</h3>
              <p className="text-slate-400 text-sm" data-id="element-591">
                Leadership time is protected for production and growth
              </p>
            </div>
          </div>

          <div className="p-6 bg-amber-500/5 border border-amber-500/20 rounded-lg max-w-3xl mx-auto" data-id="element-592">
            <p className="text-amber-400 font-medium text-center" data-id="element-593">
              Summit does not change the recruiting system — it changes who runs
              it.
            </p>
          </div>
        </div>
      </section>

      {/* 4. OWNERSHIP MAP */}
      <section className="py-20 px-6 md:px-12 max-w-6xl mx-auto" data-id="element-594">
        <h2 className="text-3xl font-bold text-white mb-12 text-center" data-id="element-595">
          Ownership Map
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16" data-id="element-596">
          {/* HIRE Owns */}
          <div className="bg-slate-900/40 border border-amber-500/20 rounded-xl p-8 relative overflow-hidden" data-id="element-597">
            <div className="absolute top-0 left-0 w-full h-1 bg-amber-500" data-id="element-598" />
            <h3 className="text-xl font-bold text-white mb-8 flex items-center" data-id="element-599">
              <span className="w-2 h-2 rounded-full bg-amber-500 mr-3" data-id="element-600" />
              HIRE Owns
            </h3>
            <ul className="space-y-4" data-id="element-601">
              {['Dedicated recruiter capacity (operated and managed by HIRE)', 'Interview execution and scheduling', 'Candidate follow-up and throughput', 'Recruiting execution accountability'].map((item, idx) => <li key={idx} className="flex items-start text-slate-300" data-id="element-602">
                  <Check className="w-5 h-5 text-amber-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-603" />
                  <span data-id="element-604">{item}</span>
                </li>)}
            </ul>
          </div>

          {/* You Own */}
          <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-8 relative overflow-hidden" data-id="element-605">
            <div className="absolute top-0 left-0 w-full h-1 bg-slate-600" data-id="element-606" />
            <h3 className="text-xl font-bold text-white mb-8 flex items-center" data-id="element-607">
              <span className="w-2 h-2 rounded-full bg-slate-600 mr-3" data-id="element-608" />
              You Own
            </h3>
            <ul className="space-y-4" data-id="element-609">
              {['Final hiring approvals', 'Sales leadership and production', 'Culture and performance expectations'].map((item, idx) => <li key={idx} className="flex items-start text-slate-400" data-id="element-610">
                  <div className="w-5 h-5 rounded-full bg-slate-800 border border-slate-700 mr-3 mt-0.5 flex-shrink-0 flex items-center justify-center" data-id="element-611">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-500" data-id="element-612" />
                  </div>
                  <span data-id="element-613">{item}</span>
                </li>)}
            </ul>
          </div>
        </div>

        <div className="mt-8 text-center" data-id="element-614">
          <p className="text-slate-400 font-medium" data-id="element-615">
            Summit transfers execution ownership and staffing risk to HIRE.
          </p>
        </div>
      </section>

      {/* 5. RECRUITER CAPACITY */}
      <section className="py-20 px-6 md:px-12 bg-slate-900/20 border-y border-slate-800" data-id="element-616">
        <div className="max-w-4xl mx-auto" data-id="element-617">
          <h2 className="text-3xl font-bold text-white mb-12 text-center" data-id="element-618">
            Recruiter Capacity
          </h2>

          <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-8 md:p-12" data-id="element-619">
            <div className="space-y-6" data-id="element-620">
              <div className="flex items-start" data-id="element-621">
                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center mr-4 flex-shrink-0 border border-slate-700" data-id="element-622">
                  <Check className="w-5 h-5 text-amber-400" data-id="element-623" />
                </div>
                <div data-id="element-624">
                  <h3 className="text-white font-bold mb-2" data-id="element-625">
                    Trained and Managed by HIRE
                  </h3>
                  <p className="text-slate-400 text-sm" data-id="element-626">
                    Recruiters are trained, managed, and quality-controlled by
                    HIRE to maintain consistency and professionalism.
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-627">
                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center mr-4 flex-shrink-0 border border-slate-700" data-id="element-628">
                  <Check className="w-5 h-5 text-amber-400" data-id="element-629" />
                </div>
                <div data-id="element-630">
                  <h3 className="text-white font-bold mb-2" data-id="element-631">
                    Represents Your Brand
                  </h3>
                  <p className="text-slate-400 text-sm" data-id="element-632">
                    Recruiters represent the client's brand with professionalism
                    and alignment to company values.
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-633">
                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center mr-4 flex-shrink-0 border border-slate-700" data-id="element-634">
                  <Check className="w-5 h-5 text-amber-400" data-id="element-635" />
                </div>
                <div data-id="element-636">
                  <h3 className="text-white font-bold mb-2" data-id="element-637">
                    Designed for Consistency
                  </h3>
                  <p className="text-slate-400 text-sm" data-id="element-638">
                    Capacity is designed to support consistent interview flow,
                    not heroic bursts.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. PRICING */}
      <section className="py-20 px-6 md:px-12 max-w-6xl mx-auto" data-id="element-639">
        <div className="text-center mb-12" data-id="element-640">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-641">
            Partnership Pricing
          </h2>
          <p className="text-slate-400" data-id="element-642">
            When recruiting execution becomes the constraint, HIRE runs it for
            you.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-643">
          <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-xl" data-id="element-644">
            <div className="mb-4" data-id="element-645">
              <div className="text-xs text-slate-500 uppercase tracking-widest mb-2" data-id="element-646">
                Build (One-Time)
              </div>
              <div className="text-3xl font-bold text-white" data-id="element-647">$5,000</div>
            </div>
          </div>

          <div className="bg-slate-900/40 border border-amber-500/30 p-6 rounded-xl relative" data-id="element-648">
            <div className="absolute top-0 left-0 w-full h-1 bg-amber-500 rounded-t-xl" data-id="element-649" />
            <div className="mb-4" data-id="element-650">
              <div className="text-xs text-amber-400 uppercase tracking-widest mb-2" data-id="element-651">
                System (Monthly)
              </div>
              <div className="text-3xl font-bold text-white" data-id="element-652">$2,500</div>
              <div className="text-xs text-slate-500 mt-1" data-id="element-653">/ month</div>
            </div>
          </div>

          <div className="bg-slate-900/40 border border-amber-500/30 p-6 rounded-xl relative" data-id="element-654">
            <div className="absolute top-0 left-0 w-full h-1 bg-amber-500 rounded-t-xl" data-id="element-655" />
            <div className="mb-4" data-id="element-656">
              <div className="text-xs text-amber-400 uppercase tracking-widest mb-2" data-id="element-657">
                Campaign (Monthly)
              </div>
              <div className="text-3xl font-bold text-white" data-id="element-658">$5,500</div>
              <div className="text-xs text-slate-500 mt-1" data-id="element-659">
                / month per campaign
              </div>
              <div className="text-xs text-amber-400 mt-2" data-id="element-660">
                Includes embedded recruiter
              </div>
            </div>
          </div>

          <div className="bg-slate-900/40 border border-slate-800 p-6 rounded-xl" data-id="element-661">
            <div className="mb-4" data-id="element-662">
              <div className="text-xs text-amber-400 uppercase tracking-widest mb-2" data-id="element-663">
                Performance Bonuses
              </div>
              <ul className="space-y-2 text-sm text-slate-300" data-id="element-664">
                <li className="flex items-start" data-id="element-665">
                  <span className="text-amber-400 mr-2" data-id="element-666">$250</span>
                  <span className="text-slate-400" data-id="element-667">per rep at launch</span>
                </li>
                <li className="flex items-start" data-id="element-668">
                  <span className="text-amber-400 mr-2" data-id="element-669">$250</span>
                  <span className="text-slate-400" data-id="element-670">per rep at 30 days</span>
                </li>
                <li className="flex items-start" data-id="element-671">
                  <span className="text-amber-400 mr-2" data-id="element-672">$250</span>
                  <span className="text-slate-400" data-id="element-673">per rep at 60 days</span>
                </li>
                <li className="flex items-start" data-id="element-674">
                  <span className="text-amber-400 mr-2" data-id="element-675">$250</span>
                  <span className="text-slate-400" data-id="element-676">per rep at 90 days</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-8 p-6 bg-amber-500/5 border border-amber-500/20 rounded-lg max-w-3xl mx-auto" data-id="element-677">
          <p className="text-amber-400 text-center font-medium" data-id="element-678">
            Summit pricing reflects recruiting execution ownership and recruiter
            staffing responsibility.
          </p>
        </div>
      </section>

      {/* 7. WHO SUMMIT IS FOR / NOT FOR */}
      <section className="py-20 px-6 md:px-12 bg-slate-900/20 border-y border-slate-800" data-id="element-679">
        <div className="max-w-5xl mx-auto" data-id="element-680">
          <h2 className="text-3xl font-bold text-white mb-12 text-center" data-id="element-681">
            Who Summit Is For
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-682">
            <div data-id="element-683">
              <h3 className="text-lg font-bold text-white mb-6 flex items-center" data-id="element-684">
                <Check className="w-5 h-5 text-amber-500 mr-2" data-id="element-685" />
                Summit is a fit if you:
              </h3>
              <ul className="space-y-4" data-id="element-686">
                {['Want recruiting execution off your plate', 'Believe sales leaders should be selling, not interviewing', 'Prefer external ownership over internal hiring', 'Trust HIRE to manage recruiter performance'].map((item, idx) => <li key={idx} className="text-slate-400 flex items-start" data-id="element-687">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mr-3 mt-2" data-id="element-688" />
                    {item}
                  </li>)}
              </ul>
            </div>
            <div data-id="element-689">
              <h3 className="text-lg font-bold text-white mb-6 flex items-center" data-id="element-690">
                <X className="w-5 h-5 text-slate-500 mr-2" data-id="element-691" />
                Summit is not a fit if you:
              </h3>
              <ul className="space-y-4" data-id="element-692">
                {['Want to manage recruiters internally', 'Prefer hands-on control over execution', 'Only want infrastructure or consulting'].map((item, idx) => <li key={idx} className="text-slate-400 flex items-start" data-id="element-693">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-600 mr-3 mt-2" data-id="element-694" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 8. RELATIONSHIP TO ADVANTAGE */}
      <section className="py-20 px-6 md:px-12 max-w-4xl mx-auto" data-id="element-695">
        <div className="bg-slate-900/30 border border-slate-800 rounded-xl p-8 md:p-12 text-center" data-id="element-696">
          <h2 className="text-2xl font-bold text-white mb-6" data-id="element-697">
            Progression Path
          </h2>
          <p className="text-slate-300 leading-relaxed max-w-2xl mx-auto" data-id="element-698">
            Most Summit partners start with Advantage. Summit exists when the
            system works — and execution capacity becomes the constraint.
          </p>
        </div>
      </section>

      {/* 9. CTA */}
      <section className="py-32 px-6 md:px-12 text-center border-t border-slate-800/50" data-id="element-699">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-8" data-id="element-700">
          See if Summit is the right execution model for your team.
        </h2>
        <button className="inline-flex items-center px-8 py-4 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-lg transition-all" data-id="element-701">
          Discuss Summit
          <ArrowRight className="ml-2 w-5 h-5" data-id="element-702" />
        </button>
      </section>
    </div>;
}